import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  History,
  Landmark,
  Link2,
  ListFilter,
  Tag,
  TriangleAlert,
  Undo2,
  User,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { EmptyState, ErrorState, HeaderRow, LoadingState, Pager, rowLink, TableShell, TH } from "@/components/PageUi";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCalendarDate, formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import {
  AccountSelect,
  accountLabel,
  BooksPeriodScreen,
  money,
  num,
  SOURCE_LABEL,
  sourceHref,
  TaxRateSelect,
  useChart,
  useTaxRates,
} from "./shared";
import { usePeriodLink, type Period } from "./period";
import { BankLineGroups } from "./BankLineGroups";
import { LinesTable } from "./GeneralLedger";
import { useIsDemo } from "@/demo/DemoContext";
import { AddBankTransaction, DemoTag } from "@/demo/AddBankTransaction";
import type { BankLine, BankLineConfidence, BankLineStatus, Book, LedgerAccount } from "./types";

type StatusFilter = BankLineStatus | "all" | "groups";

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "Uncategorised", label: "To review" },
  { value: "PossibleDuplicate", label: "Possible duplicates" },
  { value: "Matched", label: "Matched" },
  { value: "Categorised", label: "Categorised" },
  { value: "Ignored", label: "Ignored duplicates" },
  { value: "groups", label: "To review, by payee" },
];

const ALL_ACCOUNTS = "__all__";

// Bank account, Account and In the books get their own columns only in a wide pane; narrower (chat open), they fold
// under the transaction so the amount and actions stay in view.
const WIDE = "hidden @6xl:table-cell";

const CONFIDENCE: Record<NonNullable<BankLineConfidence>, { dot: string; label: string }> = {
  High: { dot: "bg-emerald-500", label: "High confidence" },
  Medium: { dot: "bg-amber-500", label: "Medium confidence" },
  Low: { dot: "bg-muted-foreground/50", label: "Low confidence" },
};

const SUGGESTION_SOURCE: Record<NonNullable<BankLine["categorisationMethod"]>, string> = {
  Rule: "Suggested by a rule",
  Learned: "Suggested from earlier transactions",
  Model: "Suggested",
};

/** Green, amber or grey: how sure the suggestion behind a line is. */
export function ConfidenceDot({ confidence }: { confidence?: BankLineConfidence | null }) {
  if (!confidence) return null;
  return (
    <span className="inline-flex shrink-0 items-center">
      <span aria-hidden className={cn("size-2 rounded-full", CONFIDENCE[confidence].dot)} />
      <span className="sr-only">{CONFIDENCE[confidence].label}</span>
    </span>
  );
}

export function BankLines() {
  return (
    <BooksPeriodScreen
      title="Bank transactions"
      subtitle="Every transaction on your tracked bank accounts in the period. Match each to a Dolfin payment, or file it under an account."
    >
      {/* Keyed by the period, so a new period starts again from its first page. */}
      {(book, period) => <BankLinesBody key={period.query} book={book} period={period} />}
    </BooksPeriodScreen>
  );
}

/** Counts per account for the period: they feed Home's figure and the account filter, so every write reloads them. */
export function useBankLineSummary(period: Pick<Period, "fromDate" | "toDate">) {
  const { books } = useBooks();
  return useLoad(
    () => books.GET("/v1/books/current/bank-lines/summary", { params: { query: { From: period.fromDate, To: period.toDate } } }),
    [books, period.fromDate, period.toDate],
  );
}

/** A focused field takes j and k as text, not navigation. */
function typingIn(target: EventTarget | null) {
  const el = target as HTMLElement | null;
  return Boolean(el?.closest("input, textarea, select, [contenteditable=true], [role=combobox], [role=listbox]"));
}

function BankLinesBody({ book, period }: { book: Book; period: Period }) {
  const { books } = useBooks();
  const [params, setParams] = useSearchParams();
  const summary = useBankLineSummary(period);
  const chart = useChart();
  const accounts = summary.data ?? [];
  const accountId = params.get("account");
  const ledgerAccountId = params.get("ledgerAccount");
  const status = (params.get("status") ?? "all") as StatusFilter;
  const highlight = params.get("highlight");
  // The linked line, kept after `highlight` leaves the URL, so its row stays marked and scrolled to.
  const [focus] = useState(highlight);
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [open, setOpen] = useState<BankLine | null>(null);
  const cursor = cursors[cursors.length - 1];
  const readOnly = book.status !== "Active";
  const isDemo = useIsDemo();
  // "Try it yourself" links here with ?add=1 to open the demo's Add a bank transaction.
  const [adding, setAdding] = useState(() => isDemo && params.get("add") === "1");

  const set = (key: string, value: string | null) => {
    setCursors([undefined]);
    setParams(
      (p) => {
        if (value) p.set(key, value);
        else p.delete(key);
        return p;
      },
      { replace: true },
    );
  };

  const page = useLoad(
    () =>
      books.GET("/v1/books/current/bank-lines", {
        params: {
          query: {
            BankAccountId: accountId ?? undefined,
            LedgerAccountId: ledgerAccountId ?? undefined,
            Status: status === "all" || status === "groups" ? undefined : status,
            Cursor: cursor,
            From: period.fromDate,
            To: period.toDate,
            Limit: 50,
          },
        },
      }),
    [books, accountId, ledgerAccountId, status, cursor, period.fromDate, period.toDate],
    { enabled: status !== "groups" },
  );
  // "To review" is what the filter's badge counts: uncategorised lines and possible duplicates. Duplicates are few and
  // held, so they lead the list unpaged; the uncategorised lines page beneath them.
  const duplicates = useLoad(
    () =>
      books.GET("/v1/books/current/bank-lines", {
        params: {
          query: {
            BankAccountId: accountId ?? undefined,
            LedgerAccountId: ledgerAccountId ?? undefined,
            Status: "PossibleDuplicate",
            From: period.fromDate,
            To: period.toDate,
            Limit: 100,
          },
        },
      }),
    [books, accountId, ledgerAccountId, period.fromDate, period.toDate],
    { enabled: status === "Uncategorised" },
  );

  // A link from the general ledger names a line: read it and open its sheet, and bring its row into view when it is
  // on the page shown.
  const linked = useLoad(
    () => books.GET("/v1/books/current/bank-lines/{id}", { params: { path: { id: highlight! } } }),
    [books, highlight],
    { enabled: Boolean(highlight) },
  );
  const opened = useRef<string | null>(null);
  useEffect(() => {
    const line = linked.data;
    if (!highlight || !line || opened.current === highlight) return;
    opened.current = highlight;
    setOpen(line);
    // A period change remounts this body, and a `highlight` still in the URL would open the sheet again.
    setParams(
      (p) => {
        p.delete("highlight");
        return p;
      },
      { replace: true },
    );
  }, [highlight, linked.data]);
  useEffect(() => {
    if (focus && page.data) document.getElementById(`bank-line-${focus}`)?.scrollIntoView({ block: "center" });
  }, [focus, page.data]);

  const lines = [
    ...(status === "Uncategorised" && cursors.length === 1 ? (duplicates.data?.data ?? []) : []),
    ...(page.data?.data ?? []),
  ];
  // shortcut: previous/next walk the page on screen and stop at its edges; crossing pages would need the next page loaded.
  const at = open ? lines.findIndex((l) => l.id === open.id) : -1;
  const previous = at > 0 ? lines[at - 1] : undefined;
  const next = at >= 0 && at < lines.length - 1 ? lines[at + 1] : undefined;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || typingIn(e.target)) return;
      if (e.key === "j" && next) setOpen(next);
      else if (e.key === "k" && previous) setOpen(previous);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, previous, next]);

  async function afterChange() {
    setOpen(null);
    await Promise.all([page.reload(), summary.reload(), status === "Uncategorised" ? duplicates.reload() : undefined]);
  }

  // The fake bank re-syncs as soon as the line is added; categorising follows, so look again a little later too.
  // All: a line a rule or the model files at once would leave "To review" before the prospect sees it categorised.
  function afterAdded() {
    if (status !== "all") set("status", null);
    for (const delay of [2500, 6000, 12000]) window.setTimeout(() => void afterChange(), delay);
  }

  if (summary.loading && !summary.data) return <LoadingState label="Loading your bank accounts" />;
  if (summary.error) {
    return <ErrorState message={apiErrorMessage(summary.error, "Couldn't load the bank transactions.")} onRetry={summary.reload} />;
  }
  if (accounts.length === 0) {
    return (
      <EmptyState icon={Landmark} title="No tracked bank accounts">
        Bank transactions appear here for bank accounts mapped to a connected account in setup.
      </EmptyState>
    );
  }

  const chartById = new Map((chart.data ?? []).map((a) => [a.id, a]));
  const toReview = (a: (typeof accounts)[number]) => num(a.uncategorised) + num(a.possibleDuplicate);
  const bankName = (id: string) => accounts.find((a) => a.bankAccountId === id)?.bankAccountName || "Bank account";
  const ledgerName = (id: string) => {
    const account = chartById.get(id);
    return account ? accountLabel(account) : "Account";
  };
  const filtered = Boolean(accountId || ledgerAccountId);

  return (
    <>
      <div className="mb-3 flex flex-col gap-3 @3xl:flex-row @3xl:items-center @3xl:justify-between">
        {/* Each label stays with its select when the filters wrap. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="bank-line-account" className="text-xs text-muted-foreground">
              Bank account
            </Label>
            <Select value={accountId ?? ALL_ACCOUNTS} onValueChange={(v) => set("account", v === ALL_ACCOUNTS ? null : v)}>
              <SelectTrigger id="bank-line-account" className="w-44 @3xl:w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_ACCOUNTS}>
                  All bank accounts <ReviewCount count={accounts.reduce((n, a) => n + toReview(a), 0)} />
                </SelectItem>
                {accounts.map((a) => (
                  <SelectItem key={a.bankAccountId} value={a.bankAccountId}>
                    {a.bankAccountName || "Bank account"} <ReviewCount count={toReview(a)} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="bank-line-status" className="text-xs text-muted-foreground">
              Show
            </Label>
            <Select value={status} onValueChange={(v) => set("status", v === "all" ? null : v)}>
              <SelectTrigger id="bank-line-status" className="w-44 @3xl:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {isDemo && !readOnly ? (
          <Button size="sm" variant="outline" className="self-start @3xl:self-auto" onClick={() => setAdding(true)}>
            Add a bank transaction <DemoTag />
          </Button>
        ) : null}
      </div>

      {filtered && status !== "groups" ? (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {accountId ? (
            <FilterChip label="Bank account" value={bankName(accountId)} onRemove={() => set("account", null)} />
          ) : null}
          {ledgerAccountId ? (
            <FilterChip label="Account" value={ledgerName(ledgerAccountId)} onRemove={() => set("ledgerAccount", null)} />
          ) : null}
        </div>
      ) : null}

      {isDemo ? <AddBankTransaction open={adding} onOpenChange={setAdding} onAdded={afterAdded} /> : null}

      {status === "groups" ? (
        <BankLineGroups chart={chart.data ?? []} readOnly={readOnly} onChanged={afterChange} />
      ) : page.loading && !page.data ? (
        <LoadingState label="Loading bank transactions" />
      ) : page.error ? (
        <ErrorState message={apiErrorMessage(page.error, "Couldn't load the bank transactions.")} onRetry={page.reload} />
      ) : lines.length === 0 ? (
        <EmptyState icon={CheckCircle2} title={status === "Uncategorised" ? "Nothing to review" : "No bank transactions"}>
          {status === "Uncategorised"
            ? `Every transaction dated in ${period.label}${filtered ? " in this view" : ""} is matched or categorised.`
            : status === "all" && !filtered
              ? `No bank transactions in ${period.label}.`
              : `No bank transactions in this view for ${period.label}.`}
        </EmptyState>
      ) : (
        <>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Date</TableHead>
                  <TableHead className={cn(TH, WIDE)}>Bank account</TableHead>
                  <TableHead className={TH}>Transaction</TableHead>
                  <TableHead className={cn(TH, WIDE)}>Account</TableHead>
                  <TableHead className={cn(TH, WIDE)}>In the books</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                  {!readOnly ? <TableHead className={TH} /> : null}
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {lines.map((l) => {
                  const row = rowLink((e?: React.MouseEvent) => {
                    // Links, filters and actions in the row (and their portalled menus) keep their own click.
                    if (e && (e.target as HTMLElement).closest("a, button, [role=menuitem], [role=option]")) return;
                    setOpen(l);
                  });
                  return (
                    <TableRow
                      key={l.id}
                      id={`bank-line-${l.id}`}
                      {...row}
                      className={cn(
                        row.className,
                        l.id === focus && "bg-amber-500/10 hover:bg-amber-500/15",
                        l.id === open?.id && "bg-muted/60",
                      )}
                    >
                      <TableCell className="whitespace-nowrap tabular-nums">{formatCalendarDate(l.bookedAt)}</TableCell>
                      <TableCell className={cn("max-w-[11rem]", WIDE)}>
                        <button
                          type="button"
                          onClick={() => set("account", l.bankAccountId)}
                          className="block max-w-full truncate text-left underline-offset-4 hover:underline focus-visible:underline focus-visible:outline-none"
                          title={l.bankAccountName ?? undefined}
                        >
                          {l.bankAccountName || "Bank account"}
                        </button>
                      </TableCell>
                      {/* Takes the slack and truncates, so a narrow pane (chat open) never pushes the actions out of view. */}
                      <TableCell className="w-full max-w-0">
                        <div className="truncate font-medium">{l.counterpartyName || l.description || "Bank transaction"}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {[l.counterpartyName ? l.description : null, l.reference].filter(Boolean).join(" · ") || " "}
                        </div>
                        <div className="mt-1 flex min-w-0 items-center gap-3 @6xl:hidden">
                          <LineAccount line={l} chart={chartById} onFilter={(id) => set("ledgerAccount", id)} />
                          <span className="min-w-0 shrink-[2] truncate">
                            <LineStatus line={l} />
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className={cn("max-w-[12rem]", WIDE)}>
                        <LineAccount line={l} chart={chartById} onFilter={(id) => set("ledgerAccount", id)} />
                      </TableCell>
                      <TableCell className={cn("max-w-[16rem]", WIDE)}>
                        <LineStatus line={l} />
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right font-medium whitespace-nowrap tabular-nums",
                          num(l.amount) > 0 && "text-emerald-700 dark:text-emerald-400",
                        )}
                      >
                        {formatCurrency(num(l.amount), l.currency)}
                      </TableCell>
                      {!readOnly ? (
                        <TableCell className="w-px text-right">
                          <LineActions line={l} onReview={() => setOpen(l)} onChanged={afterChange} />
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableShell>
          <Pager
            page={cursors.length}
            hasPrevious={cursors.length > 1}
            hasNext={Boolean(page.data?.meta?.cursors?.next)}
            disabled={page.loading}
            onPrevious={() => setCursors((c) => c.slice(0, -1))}
            onNext={() => setCursors((c) => [...c, page.data?.meta?.cursors?.next ?? undefined])}
          />
        </>
      )}
      <ReviewSheet
        line={open}
        chart={chart.data ?? []}
        readOnly={readOnly}
        onPrevious={previous ? () => setOpen(previous) : undefined}
        onNext={next ? () => setOpen(next) : undefined}
        onClose={() => setOpen(null)}
        onChanged={afterChange}
      />
    </>
  );
}

function ReviewCount({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="ml-1.5 rounded-full bg-amber-500/15 px-1.5 text-[10px] font-semibold tabular-nums text-amber-800 dark:text-amber-300">
      {count}
      <span className="sr-only"> to review</span>
    </span>
  );
}

function FilterChip({ label, value, onRemove }: { label: string; value: string; onRemove: () => void }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-full border bg-muted/40 py-0.5 pr-1 pl-2.5 text-xs">
      <span className="text-muted-foreground">{label}:</span>
      <span className="truncate font-medium">{value}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove the ${label.toLowerCase()} filter`}
        className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <X className="size-3" />
      </button>
    </span>
  );
}

/** How the line came to sit where it does: the icon before its account, with words on hover. */
function howSet(line: BankLine): { icon: ReactNode; label: string } | null {
  const muted = "size-3.5 shrink-0 text-muted-foreground/70";
  const strong = "size-3.5 shrink-0 text-foreground/80";
  if (line.status === "Matched") {
    return line.resolvedBy === "Auto"
      ? { icon: <Link2 className={muted} />, label: "Matched automatically" }
      : { icon: <Link2 className={strong} />, label: "Matched by a person" };
  }
  if (line.status === "Categorised") {
    if (line.resolvedBy !== "Auto") return { icon: <User className={strong} />, label: "Filed by a person" };
    if (line.categorisationMethod === "Rule")
      return { icon: <ListFilter className={strong} />, label: line.ruleName ? `Rule: ${line.ruleName}` : "Filed by a rule" };
    if (line.categorisationMethod === "Learned")
      return { icon: <History className={strong} />, label: "Filed like earlier transactions from this payee" };
    return { icon: <Tag className={muted} />, label: "Categorised automatically" };
  }
  if (line.status === "Uncategorised") {
    if (line.suggestedLedgerAccountId && line.confidence) {
      const from = SUGGESTION_SOURCE[line.categorisationMethod ?? "Model"];
      return { icon: <ConfidenceDot confidence={line.confidence} />, label: `${from} · ${CONFIDENCE[line.confidence].label.toLowerCase()}` };
    }
    return { icon: <span aria-hidden className="size-2 shrink-0 rounded-full bg-amber-500" />, label: "Needs review" };
  }
  return null;
}

function HowSetIcon({ line }: { line: BankLine }) {
  const how = howSet(line);
  if (!how) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} aria-label={how.label} className="inline-flex size-4 shrink-0 items-center justify-center rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
          {how.icon}
        </span>
      </TooltipTrigger>
      <TooltipContent>{how.label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Where the line sits in the ledger. An uncategorised line with a suggestion shows the suggestion, which is not where it
 * sits yet, so only accounts it is posted to filter the list.
 */
function LineAccount({
  line,
  chart,
  onFilter,
}: {
  line: BankLine;
  chart: Map<string | undefined, LedgerAccount>;
  /** Absent in the side panel, where the account is shown, not a filter. */
  onFilter?: (ledgerAccountId: string) => void;
}) {
  const name = (id: string) => {
    const account = chart.get(id);
    return account ? accountLabel(account) : "Account";
  };
  const suggested = line.status === "Uncategorised" ? line.suggestedLedgerAccountId : null;
  const posted = line.postedLedgerAccountId;
  return (
    <span className="flex min-w-0 items-center gap-1.5 text-xs">
      <HowSetIcon line={line} />
      {suggested ? (
        <span className="min-w-0 truncate">
          <span className="text-muted-foreground">Suggested: </span>
          {name(suggested)}
        </span>
      ) : posted && !onFilter ? (
        <span className="min-w-0 truncate">{name(posted)}</span>
      ) : posted && onFilter ? (
        <button
          type="button"
          onClick={() => onFilter(posted)}
          className="min-w-0 truncate text-left underline-offset-4 hover:underline focus-visible:underline focus-visible:outline-none"
        >
          {name(posted)}
        </button>
      ) : (
        <span className="text-muted-foreground">-</span>
      )}
    </span>
  );
}

/** "Matched to INV-00029 · Bean & Leaf", linked to the document's payments. */
function matchedTo(line: BankLine, links: ReturnType<typeof useBooks>["links"]) {
  if (line.matchedDocumentType && line.matchedDocumentId) {
    const href = sourceHref(links, line.matchedDocumentType, line.matchedDocumentId);
    const what = line.matchedDocumentNumber || SOURCE_LABEL[line.matchedDocumentType].toLowerCase();
    const label = `Matched to ${[what, line.matchedCounterpartyName].filter(Boolean).join(" · ")}`;
    return { label, href: href ? `${href}#payments` : null };
  }
  const type = line.matchedSourceType;
  return {
    label: `Matched to ${type === "StripePayout" ? "Stripe payout" : type ? SOURCE_LABEL[type].toLowerCase() : "a payment"}`,
    href: sourceHref(links, type ?? undefined, line.matchedSourceId ?? undefined),
  };
}

function LineStatus({ line }: { line: BankLine }) {
  const { links } = useBooks();
  switch (line.status) {
    case "Uncategorised":
      return <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Uncategorised</span>;
    case "PossibleDuplicate":
      return <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Possible duplicate</span>;
    case "Ignored":
      return <span className="text-xs text-muted-foreground">Duplicate, kept out</span>;
    case "Categorised":
      return <span className="text-xs text-muted-foreground">Categorised</span>;
    case "Matched": {
      const { label, href } = matchedTo(line, links);
      return (
        <span className="block truncate text-xs text-emerald-700 dark:text-emerald-400" title={label}>
          {href ? (
            <Link to={href} className="underline-offset-4 hover:underline">
              {label}
            </Link>
          ) : (
            label
          )}
        </span>
      );
    }
    default:
      return null;
  }
}

function LineActions({ line, onReview, onChanged }: { line: BankLine; onReview: () => void; onChanged: () => Promise<void> }) {
  const { books, canManage } = useBooks();
  const [busy, setBusy] = useState(false);

  async function run(call: () => Promise<{ error?: unknown }>, done: string, failed: string) {
    setBusy(true);
    const { error } = await call();
    setBusy(false);
    if (error) {
      toast.error(apiErrorMessage(error, failed));
      return;
    }
    toast.success(done);
    await onChanged();
  }
  const path = { params: { path: { id: line.id } } };

  if (line.status === "Uncategorised") {
    // Stacked in a narrow pane, so one row's two buttons don't widen the action column for every row.
    return (
      <div className="flex flex-col gap-1.5 @4xl:flex-row @4xl:justify-end">
        {line.suggestedLedgerAccountId && canManage ? (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() =>
              run(
                () => books.POST("/v1/books/current/bank-lines/{id}/accept-suggestion", path),
                "Categorised as suggested.",
                "Couldn't accept the suggestion.",
              )
            }
          >
            {busy ? <Spinner className="size-4" /> : <Check className="size-4" />} Accept
          </Button>
        ) : null}
        <Button size="sm" onClick={onReview}>
          Review
        </Button>
      </div>
    );
  }
  if (line.status === "PossibleDuplicate") {
    const confirm = (isDuplicate: boolean) =>
      run(
        () => books.POST("/v1/books/current/bank-lines/{id}/confirm-duplicate", { ...path, body: { isDuplicate } }),
        isDuplicate ? "Kept out of the books as a duplicate." : "Posted as a real transaction.",
        "Couldn't update the transaction.",
      );
    return (
      <div className="flex flex-wrap justify-end gap-1.5">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => confirm(false)}>
          Not a duplicate
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => confirm(true)}>
          {busy ? <Spinner className="size-4" /> : <Copy className="size-4" />} Duplicate
        </Button>
      </div>
    );
  }
  if (line.status === "Matched" || line.status === "Categorised" || line.status === "Ignored") {
    return (
      <Button
        size="sm"
        variant="ghost"
        disabled={busy}
        onClick={() =>
          run(
            () => books.POST("/v1/books/current/bank-lines/{id}/reset", path),
            line.status === "Ignored" ? "Back to possible duplicate." : "Back in Uncategorised.",
            "Couldn't undo the transaction.",
          )
        }
      >
        {busy ? <Spinner className="size-4" /> : <Undo2 className="size-4" />} Undo
      </Button>
    );
  }
  return null;
}

function ReviewSheet({
  line,
  chart,
  readOnly,
  onPrevious,
  onNext,
  onClose,
  onChanged,
}: {
  line: BankLine | null;
  chart: LedgerAccount[];
  readOnly: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  return (
    <Sheet open={line !== null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full gap-0 overflow-y-auto sm:max-w-lg">
        {!line ? null : (
          <>
            <LineSheetHeader line={line} onPrevious={onPrevious} onNext={onNext} />
            {/* Keyed by the line, so moving to the next one starts its review afresh. */}
            {line.status === "Uncategorised" && !readOnly ? (
              <Review key={line.id} line={line} chart={chart} onChanged={onChanged} />
            ) : (
              <ResolvedLine key={line.id} line={line} chart={chart} readOnly={readOnly} onChanged={onChanged} />
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

/** A line that needs no review: where it stands, and its undo. */
function ResolvedLine({
  line,
  chart,
  readOnly,
  onChanged,
}: {
  line: BankLine;
  chart: LedgerAccount[];
  readOnly: boolean;
  onChanged: () => Promise<void>;
}) {
  const byId = new Map(chart.map((a) => [a.id, a]));
  return (
    <div className="space-y-6 p-4 text-sm">
      <section className="space-y-2">
        <h3 className="text-sm font-semibold">In the books</h3>
        <div className="space-y-1.5 rounded-lg border p-3">
          <LineStatus line={line} />
          <LineAccount line={line} chart={byId} />
        </div>
        {!readOnly ? (
          <div className="flex justify-start">
            <LineActions line={line} onReview={() => undefined} onChanged={onChanged} />
          </div>
        ) : null}
      </section>
      <FromTheBank line={line} />
      <LineJournalEntry line={line} />
    </div>
  );
}

function LineSheetHeader({ line, onPrevious, onNext }: { line: BankLine; onPrevious?: () => void; onNext?: () => void }) {
  return (
    <SheetHeader className="flex-row items-start gap-3 border-b pr-12">
      <div className="min-w-0 flex-1 space-y-1.5">
        <SheetTitle className="truncate">{line.counterpartyName || line.description || "Bank transaction"}</SheetTitle>
        <SheetDescription>
          {formatCalendarDate(line.bookedAt)} ·{" "}
          <span className={cn("font-medium tabular-nums", num(line.amount) > 0 && "text-emerald-700 dark:text-emerald-400")}>
            {formatCurrency(num(line.amount), line.currency)}
          </span>
        </SheetDescription>
      </div>
      <div className="flex shrink-0 gap-1">
        <Button size="icon" variant="outline" className="size-7" disabled={!onPrevious} onClick={onPrevious} aria-label="Previous transaction (k)">
          <ChevronUp className="size-4" />
        </Button>
        <Button size="icon" variant="outline" className="size-7" disabled={!onNext} onClick={onNext} aria-label="Next transaction (j)">
          <ChevronDown className="size-4" />
        </Button>
      </div>
    </SheetHeader>
  );
}

/** What the bank sent, as it sent it. */
function FromTheBank({ line }: { line: BankLine }) {
  const rows: Array<[string, string | null | undefined]> = [
    ["Counterparty", line.counterpartyName],
    ["Description", line.description],
    ["Reference", line.reference],
    ["Bank account", line.bankAccountName],
    ["Booked", formatCalendarDate(line.bookedAt)],
  ];
  return (
    <section className="space-y-2">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold">
        <Landmark className="size-4 text-muted-foreground" /> From the bank
      </h3>
      <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1.5 rounded-lg border p-3 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="min-w-0 break-words">{value || <span className="text-muted-foreground">-</span>}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * The live entries that put the line in the books, each with a way into the general ledger. A categorised line has two:
 * the bank side parked in Uncategorised when it arrived, then the categorisation moving it out to the chosen account.
 */
function LineJournalEntry({ line }: { line: BankLine }) {
  const id = line.journalEntryId ?? null;
  const bankSide = line.status === "Categorised" ? (line.suspenseEntryId ?? null) : null;
  if (bankSide && id && bankSide !== id)
    return (
      <>
        <JournalEntrySection id={bankSide} title="Journal entry: from the bank" />
        <JournalEntrySection id={id} title="Journal entry: categorisation" />
      </>
    );
  return <JournalEntrySection id={id} title="Journal entry" />;
}

function JournalEntrySection({ id, title }: { id: string | null; title: string }) {
  const { books } = useBooks();
  const withPeriod = usePeriodLink();
  const entry = useLoad(
    () => books.GET("/v1/books/current/journal-entries/{id}", { params: { path: { id: id! } } }),
    [books, id],
    { enabled: id !== null },
  );
  if (!id) return null;
  const e = entry.data;
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        <Link
          to={withPeriod(`/books/ledger?entry=${id}`)}
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          Open in the general ledger <ExternalLink className="size-3" />
        </Link>
      </div>
      {entry.loading && !e ? (
        <LoadingState label="Loading the entry" />
      ) : entry.error ? (
        <ErrorState message={apiErrorMessage(entry.error, "Couldn't load the journal entry.")} onRetry={entry.reload} />
      ) : !e ? null : (
        <>
          <p className="text-xs text-muted-foreground">
            {formatCalendarDate(e.entryDate ?? "")} · {e.description || "Journal entry"}
          </p>
          <LinesTable lines={e.lines ?? []} />
        </>
      )}
    </section>
  );
}

function Review({ line, chart, onChanged }: { line: BankLine; chart: LedgerAccount[]; onChanged: () => Promise<void> }) {
  const { books } = useBooks();
  const taxRates = useTaxRates();
  const candidates = useLoad(
    () => books.GET("/v1/books/current/bank-lines/{id}/candidates", { params: { path: { id: line.id } } }),
    [books, line.id],
  );
  const { canManage } = useBooks();
  const [accountId, setAccountId] = useState<string | null>(line.suggestedLedgerAccountId ?? null);
  const [taxRateId, setTaxRateId] = useState<string | null>(line.suggestedAccountingTaxRateId ?? null);
  const [busy, setBusy] = useState<string | null>(null);
  // Shown in the sheet as well as toasted: the sheet stays open on a failure, so the reason sits beside the controls.
  const [failure, setFailure] = useState<string | null>(null);

  async function match(sourceType: NonNullable<BankLine["matchedSourceType"]>, sourceId: string) {
    setBusy(sourceId);
    setFailure(null);
    const { error } = await books.POST("/v1/books/current/bank-lines/{id}/match", {
      params: { path: { id: line.id } },
      body: { sourceType, sourceId },
    });
    setBusy(null);
    if (error) {
      const message = apiErrorMessage(error, "Couldn't match the transaction.");
      setFailure(message);
      toast.error(message);
      return;
    }
    toast.success("Matched. The transaction leaves Uncategorised.");
    await onChanged();
  }

  async function accept() {
    setBusy("accept");
    setFailure(null);
    const { error } = await books.POST("/v1/books/current/bank-lines/{id}/accept-suggestion", { params: { path: { id: line.id } } });
    setBusy(null);
    if (error) {
      const message = apiErrorMessage(error, "Couldn't accept the suggestion.");
      setFailure(message);
      toast.error(message);
      return;
    }
    toast.success("Categorised as suggested.");
    await onChanged();
  }

  async function categorise() {
    if (!accountId) return;
    setBusy("categorise");
    setFailure(null);
    const { error } = await books.POST("/v1/books/current/bank-lines/{id}/categorise", {
      params: { path: { id: line.id } },
      body: { ledgerAccountId: accountId, accountingTaxRateId: taxRateId },
    });
    setBusy(null);
    if (error) {
      const message = apiErrorMessage(error, "Couldn't categorise the transaction.");
      setFailure(message);
      toast.error(message);
      return;
    }
    toast.success("Categorised.");
    await onChanged();
  }

  const options = chart.filter((a) => a.isActive !== false);
  const list = candidates.data ?? [];
  const suggested = line.suggestedLedgerAccountId ? chart.find((a) => a.id === line.suggestedLedgerAccountId) : undefined;

  return (
    <>
      <div className="space-y-6 p-4">
        {failure ? (
          <p role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" />
            {failure}
          </p>
        ) : null}
        {suggested ? (
          <section className="flex items-center justify-between gap-3 rounded-lg border p-3">
            <div className="min-w-0 text-sm">
              <div className="flex items-center gap-1.5 font-medium">
                <ConfidenceDot confidence={line.confidence} />
                <span className="truncate">{accountLabel(suggested)}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {SUGGESTION_SOURCE[line.categorisationMethod ?? "Model"]}
                {line.ruleName && line.categorisationMethod === "Rule" ? `: ${line.ruleName}` : ""}
                {line.confidence ? ` · ${CONFIDENCE[line.confidence].label.toLowerCase()}` : ""}
                {line.autoRejectedAt ? " · undone once, so never applied automatically again" : ""}
              </div>
            </div>
            {canManage ? (
              <Button size="sm" disabled={busy !== null} onClick={accept}>
                {busy === "accept" ? <Spinner className="size-4" /> : null} Accept
              </Button>
            ) : null}
          </section>
        ) : null}
        <section className="space-y-2">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold">
            <Link2 className="size-4 text-muted-foreground" /> Suggested matches
          </h3>
          {candidates.loading && !candidates.data ? (
            <LoadingState label="Looking for payments" />
          ) : candidates.error ? (
            <ErrorState message={apiErrorMessage(candidates.error, "Couldn't load suggestions.")} onRetry={candidates.reload} />
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No payment in the books for {money(Math.abs(num(line.amount)))} within a month of this transaction.
            </p>
          ) : (
            <ul className="space-y-2">
              {list.map((c) => (
                <li key={c.sourceId} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                  <div className="min-w-0 text-sm">
                    <div className="truncate font-medium">{c.description || SOURCE_LABEL[c.sourceType]}</div>
                    <div className="text-xs text-muted-foreground">
                      {SOURCE_LABEL[c.sourceType]} · {formatCalendarDate(c.date)} · {money(c.amount)}
                    </div>
                  </div>
                  <Button size="sm" disabled={busy !== null} onClick={() => match(c.sourceType, c.sourceId)}>
                    {busy === c.sourceId ? <Spinner className="size-4" /> : null} Accept
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="space-y-3">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold">
            <Tag className="size-4 text-muted-foreground" /> Or file it under an account
          </h3>
          <AccountSelect id="categorise-account" label="Account" value={accountId} accounts={options} onChange={setAccountId} />
          <div className="space-y-1.5">
            <Label htmlFor="categorise-vat">VAT</Label>
            <TaxRateSelect id="categorise-vat" value={taxRateId} rates={taxRates.data ?? []} onChange={setTaxRateId} />
            <p className="text-xs text-muted-foreground">VAT is split out of the amount to the VAT account.</p>
          </div>
          <Button onClick={categorise} disabled={!accountId || busy !== null}>
            {busy === "categorise" ? <Spinner className="size-4" /> : null} Categorise
          </Button>
        </section>
        <FromTheBank line={line} />
        <LineJournalEntry line={line} />
      </div>
    </>
  );
}
